<template>
  <div
    class="restriktionen-date-picker"
    :style="restriktionenFarbVariablen"
    @mouseover="onKalenderMouseOver"
    @mouseleave="clearHover"
  >
    <AppDatePicker
      v-bind="$attrs"
      :value="value"
      :disabled-date="istDatumDeaktiviert"
      :get-classes="getZellenKlassen"
      @input="$emit('input', $event)"
      @close="clearHover"
      @calendar-change="clearHover"
      @panel-change="clearHover"
    >
      <!-- die Legende erscheint im Kalenderfuß nur, wenn Restriktionen vorhanden sind -->
      <template v-if="hatRestriktionen" #footer>
        <RestriktionenLegende hinweis="Gesperrte Tage sind nicht auswählbar · Grund per Hover" />
      </template>
    </AppDatePicker>

    <b-popover
      v-if="hoverDayKey"
      :key="hoverDayKey"
      :target="getHoverZiel"
      :show="true"
      triggers="manual"
      placement="top"
      boundary="viewport"
      custom-class="restriktionen-picker-popover"
    >
      <RestriktionenKalendertagHover :entries="hoverEntries" />
    </b-popover>
  </div>
</template>

<script>
import AppDatePicker from '@/components/common/app-date-picker/app-date-picker.vue';
import RestriktionenKalendertagHover from './restriktionen-kalendertag-hover.vue';
import RestriktionenLegende from './restriktionen-legende.vue';
import { RESTRIKTION_TYPE_CONFIG, sortRestriktionenByPriority } from './restriktionen.constants.js';
import { toDayKey } from './restriktionen-calendar.helper.js';

const TAG_KLASSE_PREFIX = 'restriktion-tag-';

export default {
  name: 'RestriktionenDatePickerComponent',
  components: {
    AppDatePicker,
    RestriktionenKalendertagHover,
    RestriktionenLegende,
  },
  inheritAttrs: false,
  props: {
    value: {
      type: [Date, String],
      default: null,
    },
    restriktionenByDay: {
      type: Object,
      default: () => ({}),
    },
    disabledDate: {
      type: Function,
      default: () => false,
    },
  },
  data() {
    return {
      hoverDayKey: null,
    };
  },
  computed: {
    hatRestriktionen() {
      return Object.keys(this.restriktionenByDay).length > 0;
    },
    hoverEntries() {
      return sortRestriktionenByPriority(this.restriktionenByDay[this.hoverDayKey] || []);
    },
    // die Typfarben werden als CSS-Variablen vererbt, damit das CSS ohne kopierte Farbwerte auskommt
    restriktionenFarbVariablen() {
      return Object.entries(RESTRIKTION_TYPE_CONFIG).reduce((acc, [type, config]) => {
        acc[`--restriktion-${type}-bg`] = config.bgColor;
        acc[`--restriktion-${type}-text`] = config.textColor;
        return acc;
      }, {});
    },
  },
  watch: {
    restriktionenByDay: 'clearHover',
  },
  created() {
    this.$_hoverZelle = null;
  },
  methods: {
    restriktionenFuerDatum(date) {
      return this.restriktionenByDay[toDayKey(date)] || null;
    },
    istDatumDeaktiviert(date, currentValue) {
      if (this.disabledDate(date, currentValue)) {
        return true;
      }
      return Boolean(this.restriktionenFuerDatum(date));
    },
    // liefert Typklasse und Marker-Klasse für gesperrte Tage, freie Tage bleiben unverändert
    getZellenKlassen(cellDate) {
      const entries = this.restriktionenFuerDatum(cellDate);
      if (!entries || !entries.length) {
        return '';
      }
      const type = sortRestriktionenByPriority(entries)[0].type;
      return `restriktion-cell restriktion-cell--${type} ${TAG_KLASSE_PREFIX}${toDayKey(cellDate)}`;
    },
    getHoverZiel() {
      return this.$_hoverZelle;
    },
    leseDayKey(zelle) {
      const treffer = Array.from(zelle.classList).find(klasse => klasse.startsWith(TAG_KLASSE_PREFIX));
      return treffer ? treffer.slice(TAG_KLASSE_PREFIX.length) : null;
    },
    onKalenderMouseOver(event) {
      const zelle = event.target.closest('td.cell.restriktion-cell');
      const dayKey = zelle ? this.leseDayKey(zelle) : null;
      if (!zelle || !dayKey) {
        this.clearHover();
        return;
      }
      // das native title-Attribut des Datepickers würde sonst zusätzlich zum Popover erscheinen
      zelle.removeAttribute('title');
      if (this.$_hoverZelle === zelle && this.hoverDayKey === dayKey) {
        return;
      }
      this.$_hoverZelle = zelle;
      this.hoverDayKey = dayKey;
    },
    clearHover() {
      this.$_hoverZelle = null;
      if (this.hoverDayKey !== null) {
        this.hoverDayKey = null;
      }
    },
  },
};
</script>

<style scoped>
/*
  Nur die Einfärbung der Restriktionstage liegt hier, das Grundaussehen kommt aus AppDatePicker
*/
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell:hover) {
  font-weight: 600;
  cursor: default;
}

:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-abreise-hart),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-abreise-hart:hover) {
  background-color: var(--restriktion-keine-abreise-hart-bg);
  color: var(--restriktion-keine-abreise-hart-text);
}

:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-abreise),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-abreise:hover) {
  background-color: var(--restriktion-keine-abreise-bg);
  color: var(--restriktion-keine-abreise-text);
}

:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-reisen),
:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell--keine-reisen:hover) {
  background-color: var(--restriktion-keine-reisen-bg);
  color: var(--restriktion-keine-reisen-text);
}

:deep(.app-date-picker .mx-calendar-content td.cell.restriktion-cell.not-current-month) {
  opacity: 0.45;
}
</style>

<style>
.restriktionen-picker-popover.popover {
  z-index: 2100;
  max-width: 380px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-lg);
}

.restriktionen-picker-popover .popover-body {
  padding: 16px;
}
</style>
